import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Leaf, ChevronLeft, LogOut, Plus, Send, FileText, Inbox, MessageSquare, CheckSquare, Package, ExternalLink, ShieldCheck, Loader2, UserPlus, Copy, Lock, Paperclip, Download } from "lucide-react";
import api, { API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Loading } from "../components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function Workspace() {
  const { eid } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { user, checking, logout } = useAuth();

  const { data: ws, isLoading, isError } = useQuery({ queryKey: ["workspace", eid], queryFn: async () => (await api.get(`/workspace/${eid}`)).data, enabled: !!eid && !checking && !!user });

  const [inviteOpen, setInviteOpen] = useState(false);
  const [invite, setInvite] = useState({ email: "", name: "" });
  const [inviteResult, setInviteResult] = useState(null);
  const [drOpen, setDrOpen] = useState(false);
  const [dr, setDr] = useState({ title: "", description: "", due_date: "" });
  const [message, setMessage] = useState("");
  const [uploading, setUploading] = useState(false);
  const [respondFor, setRespondFor] = useState(null);
  const [respondText, setRespondText] = useState("");

  const inviteMut = useMutation({ mutationFn: () => api.post(`/workspace/${eid}/invite`, invite), onSuccess: (r) => { setInviteResult(r.data); setInvite({ email: "", name: "" }); } });
  const createDr = useMutation({ mutationFn: () => api.post(`/workspace/${eid}/data-requests`, { ...dr, due_date: dr.due_date || null }), onSuccess: () => { qc.invalidateQueries({ queryKey: ["workspace", eid] }); setDrOpen(false); setDr({ title: "", description: "", due_date: "" }); toast.success("Data request raised"); } });
  const respondDr = useMutation({ mutationFn: () => api.post(`/workspace/${eid}/data-requests/${respondFor.id}/respond`, { response_text: respondText }), onSuccess: () => { qc.invalidateQueries({ queryKey: ["workspace", eid] }); setRespondFor(null); setRespondText(""); toast.success("Response submitted"); } });
  const postMsg = useMutation({ mutationFn: () => api.post(`/workspace/${eid}/messages`, { text: message }), onSuccess: () => { qc.invalidateQueries({ queryKey: ["workspace", eid] }); setMessage(""); } });

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/workspace/${eid}/files`, { name: data.filename, file_id: data.id });
      qc.invalidateQueries({ queryKey: ["workspace", eid] });
      toast.success(`Uploaded ${data.filename}`);
    } catch { toast.error("Upload failed"); } finally { setUploading(false); e.target.value = ""; }
  };

  if (checking) return <div className="min-h-screen"><Loading /></div>;
  if (!user) { nav("/login"); return null; }
  if (isLoading) return <div className="min-h-screen"><Loading label="Loading workspace" /></div>;
  if (isError || !ws) return <div className="min-h-screen flex items-center justify-center text-slate-500">Workspace not available.</div>;

  const isClient = ws.is_client;
  const eng = ws.engagement;

  const TABS = [
    ["overview", "Overview"], ["tasks", "Tasks"], ["data", "Data Requests"], ["files", "Files"],
    ["messages", "Messages"], ["approvals", "Approvals"], ["deliverables", "Deliverables"], ["lestari", "Lestari"],
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-slate-900 text-white px-4 sm:px-6 py-3 flex items-center justify-between sticky top-0 z-20">
        <div className="flex items-center gap-3 min-w-0">
          {!isClient && <button onClick={() => nav("/engagements")} className="text-slate-400 hover:text-white"><ChevronLeft className="w-5 h-5" /></button>}
          <div className="w-8 h-8 rounded-lg bg-emerald-500 flex items-center justify-center flex-shrink-0"><Leaf className="w-4 h-4 text-white" /></div>
          <div className="min-w-0">
            <div className="font-display font-semibold text-sm truncate">{eng.title}</div>
            <div className="text-[10px] text-slate-400 font-mono uppercase tracking-wider">Client Workspace {isClient && "· Client view"}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isClient && <Button size="sm" onClick={() => { setInviteResult(null); setInviteOpen(true); }} data-testid="invite-client" className="bg-emerald-600 hover:bg-emerald-700 h-8"><UserPlus className="w-4 h-4 mr-1.5" /> Invite client</Button>}
          <button onClick={async () => { await logout(); nav("/login"); }} data-testid="ws-logout" className="p-2 text-slate-400 hover:text-white"><LogOut className="w-4 h-4" /></button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        <Tabs defaultValue="overview">
          <TabsList className="mb-6 flex-wrap h-auto">
            {TABS.map(([v, l]) => <TabsTrigger key={v} value={v} data-testid={`ws-tab-${v}`}>{l}</TabsTrigger>)}
          </TabsList>

          <TabsContent value="overview">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <StatBox label="Status" value={eng.status} />
              <StatBox label="Tasks" value={`${eng.tasks.filter((t) => t.status === "done").length}/${eng.tasks.length}`} />
              <StatBox label="Open data requests" value={ws.data_requests.filter((d) => d.status === "open").length} />
            </div>
            <div className="mt-6 bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6">
              <h3 className="font-display font-semibold text-slate-900 mb-2">Welcome to your shared workspace</h3>
              <p className="text-sm text-slate-500 leading-relaxed">This is the collaborative space for <strong>{eng.org_name}</strong>. Track tasks, respond to data requests, exchange files and messages, review deliverables and approvals. Internal adviser-only items are hidden from the client view.</p>
            </div>
          </TabsContent>

          <TabsContent value="tasks">
            <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6 space-y-2">
              {eng.tasks.map((t) => (
                <div key={t.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                  <span className={`text-sm ${t.status === "done" ? "text-slate-400 line-through" : "text-slate-700"}`}>{t.name}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">{t.status.replace("_", " ")}</span>
                </div>
              ))}
              {eng.tasks.length === 0 && <p className="text-sm text-slate-400">No tasks yet.</p>}
            </div>
          </TabsContent>

          <TabsContent value="data">
            {!isClient && <div className="flex justify-end mb-4"><Button onClick={() => setDrOpen(true)} data-testid="new-data-request" className="bg-emerald-600 hover:bg-emerald-700"><Plus className="w-4 h-4 mr-1.5" /> Raise data request</Button></div>}
            <div className="space-y-3">
              {ws.data_requests.map((d) => (
                <div key={d.id} className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-5" data-testid={`data-request-${d.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2"><Inbox className="w-4 h-4 text-emerald-600" /><span className="font-medium text-slate-900">{d.title}</span></div>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${d.status === "open" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>{d.status}</span>
                  </div>
                  {d.description && <p className="text-sm text-slate-500 mt-1.5">{d.description}</p>}
                  {d.due_date && <p className="text-xs text-slate-400 mt-1">Due {d.due_date}</p>}
                  {d.response_text && <div className="mt-3 p-3 rounded-lg bg-slate-50 border border-slate-100 text-sm text-slate-700"><span className="text-xs text-slate-400 block mb-1">Response from {d.responded_by}:</span>{d.response_text}</div>}
                  {isClient && d.status === "open" && <Button size="sm" variant="outline" className="mt-3" onClick={() => { setRespondFor(d); setRespondText(""); }} data-testid={`respond-${d.id}`}>Respond</Button>}
                </div>
              ))}
              {ws.data_requests.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">No data requests yet.</p>}
            </div>
          </TabsContent>

          <TabsContent value="files">
            <div className="flex items-center gap-2 mb-4">
              <label className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 cursor-pointer transition-colors" data-testid="ws-upload-file">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />} Upload file
                <input type="file" className="hidden" onChange={uploadFile} disabled={uploading} />
              </label>
              <span className="text-xs text-slate-400">PDF, DOCX, XLSX, images — up to 15 MB</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {ws.files.map((f) => (
                <div key={f.id} className="bg-white rounded-xl border border-slate-200/80 shadow-sm p-4 flex items-center gap-3">
                  <FileText className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0"><div className="text-sm font-medium text-slate-800 truncate">{f.name}</div><div className="text-xs text-slate-400">by {f.uploaded_by}</div></div>
                  {f.internal && <span className="text-[10px] flex items-center gap-1 text-slate-500"><Lock className="w-3 h-3" /> internal</span>}
                  {f.file_id && <a href={`${API}/files/${f.file_id}/download`} target="_blank" rel="noreferrer" className="text-emerald-600 hover:text-emerald-700" data-testid={`download-${f.id}`}><Download className="w-4 h-4" /></a>}
                </div>
              ))}
              {ws.files.length === 0 && <p className="text-sm text-slate-400 py-6 col-span-2 text-center">No files shared yet.</p>}
            </div>
          </TabsContent>

          <TabsContent value="messages">
            <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6">
              <div className="space-y-3 max-h-96 overflow-y-auto mb-4">
                {ws.messages.map((m) => (
                  <div key={m.id} className={m.author_role === (isClient ? "client" : "adviser") ? "flex justify-end" : "flex justify-start"}>
                    <div className={`max-w-[75%] px-3.5 py-2 rounded-2xl text-sm ${m.author_role === "adviser" ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-800"}`}>
                      <div className="text-[10px] opacity-70 mb-0.5">{m.author}</div>{m.text}
                    </div>
                  </div>
                ))}
                {ws.messages.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">No messages yet. Start the conversation.</p>}
              </div>
              <div className="flex gap-2">
                <Input value={message} onChange={(e) => setMessage(e.target.value)} data-testid="ws-message-input" placeholder="Write a message…" onKeyDown={(e) => { if (e.key === "Enter" && message) postMsg.mutate(); }} />
                <Button onClick={() => postMsg.mutate()} disabled={!message} data-testid="ws-message-send" className="bg-emerald-600 hover:bg-emerald-700"><Send className="w-4 h-4" /></Button>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="approvals">
            <div className="space-y-3">
              {ws.approvals.map((a) => (
                <div key={a.id} className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-5 flex items-center justify-between">
                  <div className="flex items-center gap-2"><CheckSquare className="w-4 h-4 text-emerald-600" /><div><div className="font-medium text-slate-900 text-sm">{a.title}</div><div className="text-xs text-slate-400">{a.description}</div></div></div>
                  {a.status === "pending" && isClient ? (
                    <div className="flex gap-2">
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-8" onClick={() => decide(a.id, "approved")} data-testid={`approve-${a.id}`}>Approve</Button>
                      <Button size="sm" variant="outline" className="h-8" onClick={() => decide(a.id, "rejected")}>Reject</Button>
                    </div>
                  ) : <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${a.status === "approved" ? "bg-emerald-100 text-emerald-700" : a.status === "rejected" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`}>{a.status}</span>}
                </div>
              ))}
              {ws.approvals.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">No approvals requested.</p>}
            </div>
          </TabsContent>

          <TabsContent value="deliverables">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {ws.deliverables.map((d) => (
                <div key={d.id} className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-5">
                  <Package className="w-5 h-5 text-emerald-600 mb-2" />
                  <div className="font-medium text-slate-900 text-sm">{d.name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{d.description}</div>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 mt-2 inline-block">{d.status}</span>
                </div>
              ))}
              {ws.deliverables.length === 0 && <p className="text-sm text-slate-400 py-6 col-span-2 text-center">No deliverables yet.</p>}
            </div>
          </TabsContent>

          <TabsContent value="lestari">
            <LestariTab eid={eid} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Invite dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Invite client to workspace</DialogTitle></DialogHeader>
          {inviteResult ? (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">Client account created. Share these credentials securely:</p>
              <div className="p-4 rounded-xl bg-slate-900 text-white font-mono text-sm space-y-1">
                <div>Email: {inviteResult.email}</div>
                <div className="flex items-center gap-2">Password: {inviteResult.temp_password}
                  <button onClick={() => { navigator.clipboard.writeText(inviteResult.temp_password); toast.success("Copied"); }}><Copy className="w-3.5 h-3.5" /></button>
                </div>
              </div>
              <p className="text-xs text-slate-400">They can sign in at the login page and will land directly in this workspace.</p>
              <DialogFooter><Button onClick={() => setInviteOpen(false)} className="bg-emerald-600 hover:bg-emerald-700">Done</Button></DialogFooter>
            </div>
          ) : (
            <>
              <div><Label>Name</Label><Input value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })} data-testid="invite-name" className="mt-1.5" /></div>
              <div><Label>Email</Label><Input value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} data-testid="invite-email" className="mt-1.5" /></div>
              <DialogFooter><Button variant="outline" onClick={() => setInviteOpen(false)}>Cancel</Button><Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => inviteMut.mutate()} disabled={!invite.email || !invite.name || inviteMut.isPending} data-testid="invite-send">{inviteMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create & invite"}</Button></DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Data request dialog */}
      <Dialog open={drOpen} onOpenChange={setDrOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Raise data request</DialogTitle></DialogHeader>
          <div><Label>Title</Label><Input value={dr.title} onChange={(e) => setDr({ ...dr, title: e.target.value })} data-testid="dr-title" className="mt-1.5" placeholder="e.g. 2024 energy consumption data" /></div>
          <div><Label>Description</Label><Textarea value={dr.description} onChange={(e) => setDr({ ...dr, description: e.target.value })} className="mt-1.5" /></div>
          <div><Label>Due date</Label><Input type="date" value={dr.due_date} onChange={(e) => setDr({ ...dr, due_date: e.target.value })} className="mt-1.5" /></div>
          <DialogFooter><Button variant="outline" onClick={() => setDrOpen(false)}>Cancel</Button><Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => createDr.mutate()} disabled={!dr.title} data-testid="dr-create">Raise request</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Respond dialog */}
      <Dialog open={!!respondFor} onOpenChange={(o) => !o && setRespondFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Respond to: {respondFor?.title}</DialogTitle></DialogHeader>
          <Textarea value={respondText} onChange={(e) => setRespondText(e.target.value)} data-testid="respond-text" placeholder="Provide the requested information…" className="min-h-[120px]" />
          <DialogFooter><Button variant="outline" onClick={() => setRespondFor(null)}>Cancel</Button><Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => respondDr.mutate()} disabled={!respondText} data-testid="respond-submit">Submit</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );

  function decide(aid, decision) {
    api.patch(`/workspace/${eid}/approvals/${aid}?decision=${decision}`).then(() => { qc.invalidateQueries({ queryKey: ["workspace", eid] }); toast.success(`Approval ${decision}`); });
  }
}

function StatBox({ label, value }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-5">
      <div className="text-xs text-slate-400">{label}</div>
      <div className="font-display text-2xl font-bold text-slate-900 mt-1 capitalize">{value}</div>
    </div>
  );
}

function LestariTab({ eid }) {
  const { data, isLoading } = useQuery({ queryKey: ["lestari", eid], queryFn: async () => (await api.get(`/workspace/${eid}/lestari`)).data });
  if (isLoading) return <Loading />;
  return (
    <div>
      <div className="flex items-center gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-4">
        <ShieldCheck className="w-4 h-4" /> Lestari AI Platform is <strong>simulated</strong> in this environment. Links and statuses are placeholders — no live calls are made.
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {data.modules.map((m) => (
          <div key={m.key} className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-5">
            <div className="font-medium text-slate-900">{m.name}</div>
            <span className={`text-xs px-2 py-0.5 rounded-full mt-2 inline-block ${m.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{m.status}</span>
            <a href={m.deep_link} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-sm text-emerald-600 hover:underline mt-3" data-testid={`lestari-${m.key}`}>Open in Lestari <ExternalLink className="w-3.5 h-3.5" /></a>
          </div>
        ))}
      </div>
    </div>
  );
}
